import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, UserRound } from "lucide-react";
import {
  adminTenantCreateSchema,
  type AdminTenantCreateInput
} from "@shudian/contracts";
import { adminCreateTenant } from "../api/admin.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { Button } from "../components/ui/Button.js";
import { Alert } from "../components/ui/Alert.js";
import { PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";

export function TenantCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<AdminTenantCreateInput>({
    resolver: zodResolver(adminTenantCreateSchema),
    defaultValues: {
      id: "",
      name: "",
      ownerEmail: "",
      ownerName: "",
      ownerPassword: ""
    }
  });

  const mutation = useMutation({
    mutationFn: (input: AdminTenantCreateInput) => adminCreateTenant(input),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "overview"] });
      navigate(`/tenants/${encodeURIComponent(response.tenant.id)}`);
    },
    onError: (err) => {
      setErrorMessage(err instanceof CloudApiError ? describeError(err.code) : "创建失败");
    }
  });

  const onSubmit = handleSubmit((values) => {
    setErrorMessage(null);
    const payload: AdminTenantCreateInput = {
      id: values.id.trim(),
      name: values.name.trim(),
      ownerEmail: values.ownerEmail?.trim() || undefined,
      ownerName: values.ownerName?.trim() || undefined,
      ownerPassword: values.ownerPassword || undefined
    };
    mutation.mutate(payload);
  });

  return (
    <div>
      <PageHeader
        eyebrow="客户运营 / 新增客户"
        title="新增客户"
        description="创建客户档案和登录账号。初始授权使用默认月套餐，创建后可在客户档案中调整套餐、能力和到期时间。"
        actions={
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            返回
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,680px)_minmax(280px,1fr)]">
        <SectionSurface>
          <SectionHeader title="客户资料" description="用户 ID 创建后会作为云端租户标识使用，请保持稳定。" />
          <form className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
            {errorMessage && (
              <div className="md:col-span-2">
                <Alert tone="danger">{errorMessage}</Alert>
              </div>
            )}
            <Field label="客户 ID" htmlFor="id" required error={errors.id?.message} hint="2-64 个字符，只允许字母、数字、_、-">
              <Input id="id" placeholder="tenant_xxx" invalid={!!errors.id} {...register("id")} />
            </Field>
            <Field label="客户名称" htmlFor="name" required error={errors.name?.message}>
              <Input id="name" placeholder="例如 Demo Studio" invalid={!!errors.name} {...register("name")} />
            </Field>
            <Field label="登录邮箱" htmlFor="ownerEmail" error={errors.ownerEmail?.message}>
              <Input id="ownerEmail" type="email" placeholder="可选" invalid={!!errors.ownerEmail} {...register("ownerEmail")} />
            </Field>
            <Field label="联系人姓名" htmlFor="ownerName" error={errors.ownerName?.message}>
              <Input id="ownerName" placeholder="可选" invalid={!!errors.ownerName} {...register("ownerName")} />
            </Field>
            <Field label="初始密码" htmlFor="ownerPassword" error={errors.ownerPassword?.message} hint="可选，至少 8 位；生产登录需要为用户设置密码" className="md:col-span-2">
              <Input id="ownerPassword" type="password" placeholder="可选" invalid={!!errors.ownerPassword} {...register("ownerPassword")} />
            </Field>
            <div className="flex items-center gap-2 md:col-span-2">
              <Button type="submit" disabled={mutation.isPending}>
                <Plus className="h-4 w-4" />
                {mutation.isPending ? "创建中..." : "新增客户"}
              </Button>
              <Button variant="ghost" onClick={() => navigate(-1)}>
                取消
              </Button>
            </div>
          </form>
        </SectionSurface>

        <SectionSurface>
          <SectionHeader title="创建后默认状态" />
          <div className="space-y-3 px-4 py-4 text-sm">
            <div className="rounded border border-surface-border bg-surface-raised px-3 py-3">
              <div className="flex items-center gap-2 font-medium text-text-primary">
                <UserRound className="h-4 w-4 text-accent" />
                单账号客户
              </div>
              <p className="mt-1 text-xs leading-5 text-text-secondary">
                每个客户主体对应一个登录账号。后续可在客户档案中重置密码和查看登录状态。
              </p>
            </div>
            <div className="rounded border border-surface-border bg-surface-raised px-3 py-3">
              <div className="font-medium text-text-primary">默认月套餐</div>
              <p className="mt-1 text-xs leading-5 text-text-secondary">
                创建成功后进入客户详情页，可立即修改设备数、店铺数、功能权限和到期时间。
              </p>
            </div>
          </div>
        </SectionSurface>
      </div>
    </div>
  );
}
